import React from 'react';
import { Box, Collapse, Paper, Stack, Typography } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';

interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  /** Uncontrolled initial state. Ignored when `open` is provided. */
  defaultOpen?: boolean;
  /** Controlled open state. Pair with `onOpenChange`. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Small trailing content in the header (e.g. a count chip). */
  trailing?: React.ReactNode;
  children: React.ReactNode;
}

export default function CollapsibleSection({
  title,
  subtitle,
  defaultOpen = false,
  open: openProp,
  onOpenChange,
  trailing,
  children,
}: CollapsibleSectionProps) {
  const [internalOpen, setInternalOpen] = React.useState(defaultOpen);
  const controlled = openProp !== undefined;
  const open = controlled ? openProp : internalOpen;

  const toggle = () => {
    const next = !open;
    if (!controlled) setInternalOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Paper
      elevation={0}
      sx={(t) => ({
        borderRadius: `${t.ds.radius.md}px`,
        border: `1px solid ${t.tokens.border.subtle}`,
        overflow: 'hidden',
      })}
    >
      <Box
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
        sx={{
          px: 2.5,
          py: 1.75,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: (t) => t.tokens.surface.sunken ?? 'action.hover' },
        }}
      >
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: 'text.secondary',
            transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
            transition: 'transform 0.15s ease',
          }}
        />
        <Stack sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700}>
            {title}
          </Typography>
          {subtitle ? (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          ) : null}
        </Stack>
        {trailing}
      </Box>
      <Collapse in={open} unmountOnExit>
        <Box sx={{ borderTop: (t) => `1px solid ${t.palette.divider}` }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}
